import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Alert } from 'react-onei';

import Indication from './Indication';
import Preview from './Preview';
import device from '../../helpers/breakpoints';

const CaptchaContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media ${device.mobileLandscape} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const IndicationList = styled.ol`
  margin: 0;
  padding: 0;
  min-width: 50%;

  @media ${device.mobileLandscape} {
    width: 100%;
    margin-bottom: 1rem;
  }
`;

const IndicationItem = styled.li`
  display: flex;
  width: 100%;
  margin-top: 0.1rem;
  list-style: outside none none;
`;

const ImageList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: space-between;
  width: 245px;
  max-width: 100%;
 `;

const ImageItem = styled.li`
  list-style: none;
`;

const ImageInput = styled.input`
  width: calc(100/3)%;
  margin-bottom: 2px;

  &:focus {
    outline: black dotted 2px;
  }
`;

const getIndicationStatus = (key, array) => {
    let indicationStatus = 'next';
    if (array.length > key) {
        indicationStatus = 'previous';
    }
    if (array.length - 1 === key) {
        indicationStatus = 'last';
    }
    if (array.length === key) {
        indicationStatus = 'current';
    }
    return indicationStatus;
}

const Captcha = ({ indications, images, selectedImages, errorMessage, onSelectImage, onRemoveImage }) => {
    const handleClick = (e, img) => {
        e.preventDefault();
        onSelectImage(img);
    }

    return (
        <div id="captcha-block" className="spacing-s">
            {errorMessage && <Alert mini variant="error" id="captcha-error-message" title={errorMessage} tabindex="0" className="spacing-xs" />}
            <CaptchaContainer className="spacing-s">
                <IndicationList id="captcha-indications">
                    {indications.map((indication, index) => {
                        const status = getIndicationStatus(index, selectedImages);
                        return (
                            <IndicationItem
                                id={`captcha-indication-${index + 1}`}
                                key={`captcha-indication-${index + 1}`}
                            >
                                <Indication
                                    label={`${index + 1}. ${indication}`}
                                    status={status}
                                />
                                <Preview
                                    indication={indication}
                                    image={selectedImages[index]}
                                    status={status}
                                    onClick={() => onRemoveImage()}
                                />
                            </IndicationItem>
                        );
                    })}
                </IndicationList>

                <ImageList id="captcha-images">
                    {images.map((img) => (
                        <ImageItem
                            key={`captcha-image-${img.value}`}
                        >
                            <ImageInput
                                type="image"
                                id={`captcha-image-${img.value}`}
                                src={img.data}
                                alt={`Image captcha ${img.value}`}
                                onClick={e => handleClick(e, img)}
                                tabIndex={0}
                            />
                        </ImageItem>
                    ))}
                </ImageList>
            </CaptchaContainer>
        </div>
    )
};

Captcha.defaultProps = {
    errorMessage: null,
    selectedImages: [],
    onSelectImage: () => { },
    onRemoveImage: () => { },
};

Captcha.propTypes = {
    /** Array of indications string */
    indications: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** Array of captcha images sources */
    images: PropTypes.arrayOf(PropTypes.shape({
        format: PropTypes.string,
        value: PropTypes.number,
        data: PropTypes.string,
    })).isRequired,
    /** Array of selected captcha images */
    selectedImages: PropTypes.arrayOf(PropTypes.shape({
        format: PropTypes.string,
        value: PropTypes.number,
        data: PropTypes.string,
    })).isRequired,
    /** Function handler on image select */
    onSelectImage: PropTypes.func,
    /** Function handler on image remove */
    onRemoveImage: PropTypes.func,
    /** String error message */
    errorMessage: PropTypes.string,
};

export default Captcha;
